import React, { useState, useEffect } from 'react';
import { 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Typography,
  TextField,
  FormHelperText, 
  Button,
  CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  makeStyles 
} from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers'; 
import { authFetch, logout } from '../../auth';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 150, 
    width: '100%',
  },
  formText: {
    fontSize: 14,
    color: theme.palette.primary.main,
    textAlign: "center",
    marginLeft: theme.spacing(1)
  },
  formRow: {
    width: "100%",       
    maxHeight: "50%",        
  },
  btnAction: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 5),
    marginRight: theme.spacing(2)
  }
}));

const VersionSelector = (props) => {
  const classes = useStyles();
  
  // Estados de ciclos existentes
  const [cycles, setCycles] = useState([]);
  const [cycleUsers, setCycleUsers] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState("");

  // Estado para el DatePicker interno
  const [internalPeriodDate, setInternalPeriodDate] = useState(new Date());

  // Estados para las versiones dinámicas
  const [versionesDisponibles, setVersionesDisponibles] = useState([]);
  const [versionInicial, setVersionInicial] = useState('');
  const [versionFinal, setVersionFinal] = useState('');
  const [loadingVersiones, setLoadingVersiones] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isExporting, setIsExporting] = useState(false); // Nuevo estado para loader de excel
  const [calcEnabled, setCalcEnabled] = useState(true);
  const [feedbackDialog, setFeedbackDialog] = useState(false) 
  const [feedbackMsg, setFeedbackMsg] = useState("")
  
  const handleFeedbackDialogOpen = () => {
    setFeedbackDialog(true);
  };
  
  const handleFeedbackDialogClose = () => {
    setFeedbackDialog(false);
  };

  // Carga inicial de ciclos
  useEffect(() => {
    document.title = "Liquidación de consumos";        
    
    authFetch("/api/check-billing-cycles?username=" + String(props.username) + "&user_type=" + props.userType)
      .then(response => response.status === 422 ? logout() : response.json())
      .then(response => {
        if (response && response.cycles) setCycles(response.cycles);
      });
    
    authFetch("/api/get-cycle-users?username=" + String(props.username) + "&billing=true")
      .then(response => response.status === 422 ? logout() : response.json())
      .then(response => {
        if (response && response.cycle_users) setCycleUsers(response.cycle_users);
      }); 
  }, [props.username, props.userType]);

  // Buscar versiones basado en el periodo INTERNO y el ciclo
  useEffect(() => {
    setCalcEnabled(true)
    if (!internalPeriodDate || !selectedCycle) {
      setVersionesDisponibles([]);
      return;
    }

    const fecha = new Date(internalPeriodDate);
    const year = fecha.getFullYear();
    const month = fecha.getMonth() + 1;
    const billingYear = new Date(props.billingDate).getFullYear();
    const billingMonth = new Date(props.billingDate).getMonth() + 1;
    const billingVersion = props.tariffCalcVersion;

    setLoadingVersiones(true);
    setVersionInicial('');
    setVersionFinal('');

    authFetch(`/api/get-liquidation-versions?year=${year}&month=${month}&cycle=${selectedCycle}&username=${props.username}&billing_year=${billingYear}&billing_month=${billingMonth}&version=${billingVersion}`)
      .then(response => response.json())
      .then(data => {
        if (data && data.versions) {
          setVersionesDisponibles(data.versions);
          setCalcEnabled(data.calc_enabled);
        }
        setLoadingVersiones(false);
      })
      .catch(err => {
        setFeedbackMsg("Error al obtener versiones del periodo seleccionado")
        handleFeedbackDialogOpen()
        setLoadingVersiones(false);
      });

  }, [internalPeriodDate, selectedCycle]);

  const handleCalcularAjustes = () => {
    if (!versionInicial || !versionFinal) return;

    const fechaOrigen = new Date(internalPeriodDate);
    const fechaDestino = new Date(props.billingDate);

    const payload = {
      source_year: fechaOrigen.getFullYear(),
      source_month: fechaOrigen.getMonth() + 1,
      cycle: selectedCycle,
      version_inicial: versionInicial,
      version_final: versionFinal,
      target_year: fechaDestino.getFullYear(),
      target_month: fechaDestino.getMonth() + 1,
      target_version: props.tariffCalcVersion
    };

    setIsProcessing(true);

    authFetch("/api/calculate-adjustments"+"?username="+props.username, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    })
    .then(response => {
      if (!response.ok) throw new Error();
      return response.json();
    })
    .then(data => {
      setFeedbackMsg(data.message || "Proceso terminado con éxito.");
      setIsProcessing(false);
      const fecha = new Date(internalPeriodDate);
      const year = fecha.getFullYear();
      const month = fecha.getMonth() + 1;
      const billingYear = new Date(props.billingDate).getFullYear();
      const billingMonth = new Date(props.billingDate).getMonth() + 1;
      const billingVersion = props.tariffCalcVersion;
      authFetch(`/api/get-liquidation-versions?year=${year}&month=${month}&cycle=${selectedCycle}&username=${props.username}&billing_year=${billingYear}&billing_month=${billingMonth}&version=${billingVersion}`)
      .then(response => response.json())
      .then(data => {
        if (data && data.versions) {
          setVersionesDisponibles(data.versions);
          setCalcEnabled(data.calc_enabled);
        }
      });
    })
    .catch(() => {
      setFeedbackMsg("Ocurrió un error al procesar las diferencias.");
      setIsProcessing(false);
    });
  };

  // NUEVA FUNCIÓN: Consumir binario Excel desde el Backend
  const handleExportExcel = () => {
    setIsExporting(true);
    const fechaDestino = new Date(props.billingDate);
    const targetYear = fechaDestino.getFullYear();
    const targetMonth = fechaDestino.getMonth() + 1;
    const targetVersion = props.tariffCalcVersion;

    authFetch(`/api/export-adjustments-excel?year=${targetYear}&month=${targetMonth}&cycle=${selectedCycle}&version=${targetVersion}&timestamp=${new Date().getTime()}`)
      .then(response => {
        if (!response.ok) throw new Error();
        return response.blob();
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Reporte_Ajustes_Ciclo_${selectedCycle}_${targetYear}_${targetMonth}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setIsExporting(false);
      })
      .catch(() => {
        setFeedbackMsg("Error al exportar el archivo Excel.");
        handleFeedbackDialogOpen();
        setIsExporting(false);
      });
  };

  const showFeedbackDialog = () => (
    <Dialog open={feedbackDialog} onClose={handleFeedbackDialogClose}>
      <DialogTitle style={{textAlign: 'center'}}>Aviso</DialogTitle>
      <DialogContent>
        <DialogContentText>{feedbackMsg}</DialogContentText>
      </DialogContent>
      <DialogActions>          
        <Button onClick={handleFeedbackDialogClose} color="primary" autoFocus>Aceptar</Button>
      </DialogActions>
    </Dialog>
  );

  const tieneMasDeUnaVersion = versionesDisponibles.length > 1;

  return (
    <div className={classes.container}>
      <Grid container spacing={2} justify='center' alignItems="center">
        
        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle2" align="center" color="primary">Periodo de Conceptos a Ajustar:</Typography>
          <DatePicker    
            variant="dialog"    
            inputVariant="outlined"                                                                                                                  
            openTo="month"
            format="MMMM-yyyy" 
            minDate={new Date(2021, 11, 1)}   
            maxDate={new Date(2026, 11, 1)}                                        
            views={["year", "month"]}                                            
            value={internalPeriodDate}                                             
            onChange={(date) => setInternalPeriodDate(date)}
            error={false}
            InputProps={{ classes: { input: classes.formText } }}     
            autoOk fullWidth size="small"
          /> 
        </Grid>

        <Grid item xs={12} sm={4}>
          <Typography variant="subtitle2" align="center" color="primary">Ciclo:</Typography>
          <TextField 
            select variant="outlined" size="small"
            InputProps={{ classes: { input: classes.formText } }}
            className={classes.formRow} value={selectedCycle}
            onChange={(e) => setSelectedCycle(e.target.value)}
          >
            {cycles.map((cycle, idx) => (
              cycleUsers[cycle.code] > 0 ? <MenuItem key={idx} value={cycle.code}>{cycle.code}</MenuItem> : null
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12}><hr style={{ opacity: 0.1, margin: '15px 0' }} /></Grid>

        <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" align="center" color="primary">Versión Inicial:</Typography>
            <TextField
              select variant="outlined" size="small" id="version-inicial"
              value={versionInicial} onChange={(e) => setVersionInicial(e.target.value)}
              InputProps={{ classes: { input: classes.formText } }} className={classes.formRow} 
            >
              {versionesDisponibles.map((v, idx) => <MenuItem key={idx} value={v}>{v}</MenuItem>)}
            </TextField>
            {versionesDisponibles.length === 0 && !loadingVersiones && <FormHelperText>Sin registros</FormHelperText>}
        </Grid>

        <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" align="center" color="primary">Versión Final:</Typography>
            <TextField
              select variant="outlined" size="small" id="version-final"
              value={versionFinal} onChange={(e) => setVersionFinal(e.target.value)}
              InputProps={{ classes: { input: classes.formText } }} className={classes.formRow} 
            >
              <MenuItem value="" disabled>Seleccione versión final</MenuItem>
              {versionesDisponibles.filter(v => v !== versionInicial).map((v, idx) => <MenuItem key={idx} value={v}>{v}</MenuItem>)}
            </TextField>
            {!tieneMasDeUnaVersion && <FormHelperText>Se requiere más de una versión</FormHelperText>}
        </Grid>

        {/* REQUERIMIENTO: Bloque de Botones según estado de calcEnabled */}
        <Grid item xs={12} style={{ textAlign: 'center' }}>
          <Button
            variant="contained" color="primary" className={classes.btnAction}
            onClick={handleCalcularAjustes}
            disabled={!versionInicial || !versionFinal || isProcessing || !calcEnabled}
          >
            {isProcessing ? <CircularProgress size={24} color="inherit" /> : "Calcular Ajustes"}
          </Button>

          {/* BOTÓN ADICIONAL INYECTADO */}
          {!calcEnabled && (
            <Button
              variant="contained" style={{ backgroundColor: '#2e7d32', color: '#fff' }} className={classes.btnAction}
              onClick={handleExportExcel} disabled={isExporting}
            >
              {isExporting ? <CircularProgress size={24} color="inherit" /> : "Exportar a Excel"}
            </Button>
          )}
        </Grid>

        {!calcEnabled && (
          <Grid item xs={12} style={{ textAlign: 'center' }}>
            <FormHelperText style={{ textAlign: 'center' }}>Ya se han calculado los ajustes para este periodo.</FormHelperText>
          </Grid>
        )}
      </Grid>
      {showFeedbackDialog()}
    </div>
  );
};

export default VersionSelector;